/**
 * ShowService - Secure show management following FAQ requirements
 * Handles show creation, approval system, moderation, and discovery
 */

import { supabase } from '../config/supabase';
import { executeWithRetry } from '../config/supabase';
import { Show, ShowPoster } from '../models/Show';
import CityDistanceService from './CityDistanceService';
import authService from './AuthService'; // Import authService singleton
import NetworkService from './NetworkService';
import { awardScenePoints } from './UserService';

class ShowService {
  constructor() {
    // Prevent multiple instances
    if (ShowService.instance) {
      return ShowService.instance;
    }
    ShowService.instance = this;
    
    this.shows = [];
    this.listeners = [];
    this.isLoading = false;
    this.currentLocation = null;
    this.currentRadius = 25;
    this.subscription = null;
    this.MUSIC_GENRES = [
      'rock', 'punk', 'metal', 'indie', 'alternative', 'hardcore',
      'folk', 'country', 'blues', 'jazz', 'electronic', 'hip_hop',
      'reggae', 'ska', 'grunge', 'psychedelic', 'experimental', 'other'
    ];

    // Genre mapping from frontend to database values
    this.GENRE_MAPPING = {
      'Rock': 'rock',
      'Indie Rock': 'indie',
      'Alternative Rock': 'alternative',
      'Classic Rock': 'rock',
      'Progressive Rock': 'rock',
      'Post-Rock': 'rock',
      'Shoegaze': 'rock',
      'Metal': 'metal',
      'Death Metal': 'metal',
      'Black Metal': 'metal',
      'Doom Metal': 'metal',
      'Thrash Metal': 'metal',
      'Heavy Metal': 'metal',
      'Hardcore': 'hardcore',
      'Grindcore': 'hardcore',
      'Metalcore': 'metal',
      'Deathcore': 'metal',
      'Post-Hardcore': 'hardcore',
      'Punk': 'punk',
      'Pop Punk': 'punk',
      'Hardcore Punk': 'punk',
      'Post-Punk': 'punk',
      'Punk Rock': 'punk',
      'Electronic': 'electronic',
      'House': 'electronic',
      'Techno': 'electronic',
      'Dubstep': 'electronic',
      'Drum & Bass': 'electronic',
      'Ambient': 'electronic',
      'IDM': 'electronic',
      'Trance': 'electronic',
      'EDM': 'electronic',
      'Synth-pop': 'electronic',
      'Synthwave': 'electronic',
      'Experimental Electronic': 'electronic',
      'Hip Hop': 'hip_hop',
      'Rap': 'hip_hop',
      'Trap': 'hip_hop',
      'Underground Hip Hop': 'hip_hop',
      'Conscious Rap': 'hip_hop',
      'Folk': 'folk',
      'Acoustic': 'folk',
      'Singer-Songwriter': 'folk',
      'Indie Folk': 'folk',
      'Folk Rock': 'folk',
      'Americana': 'folk',
      'Country': 'country',
      'Alt-Country': 'country',
      'Bluegrass': 'country',
      'Country Rock': 'country',
      'Outlaw Country': 'country',
      'Jazz': 'jazz',
      'Blues': 'blues',
      'Blues Rock': 'blues',
      'Jazz Fusion': 'jazz',
      'Swing': 'jazz',
      'Bebop': 'jazz',
      'R&B': 'hip_hop',
      'Soul': 'hip_hop',
      'Funk': 'hip_hop',
      'Neo-Soul': 'hip_hop',
      'Motown': 'hip_hop',
      'Reggae': 'reggae',
      'Ska': 'ska',
      'Ska Punk': 'ska',
      'Dub': 'reggae',
      'Dancehall': 'reggae',
      'Pop': 'other',
      'Indie Pop': 'indie',
      'Dream Pop': 'indie',
      'Electropop': 'electronic',
      'Art Pop': 'other',
      'Classical': 'other',
      'Experimental': 'experimental',
      'Noise': 'experimental',
      'Avant-garde': 'experimental',
      'Other': 'other'
    };
    
    this.lastLoadTime = null;
    this.paginationInfo = {
      currentPage: 1,
      pageSize: 10,
      totalShows: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPreviousPage: false
    };
  }
  
  async loadShows(location = this.currentLocation, radius = 25) {
    try {
      console.log('🎵 Loading shows for location:', location, 'radius:', radius);
      
      this.isLoading = true;
      this.notifyListeners();

      if (!location) {
        console.log('⚠️ No location provided, using default');
        location = { city: 'Austin', state: 'TX' };
      }

      // Get nearby cities within radius
      const nearbyCities = await this.getNearbyCities(location, radius);
      console.log(`📍 Found ${nearbyCities.length} cities within ${radius} miles`);

      // Build query for all nearby cities using correct column names
      let query = supabase
        .from('shows')
        .select(`
          id,
          title,
          description,
          band_name,
          venue_name,
          venue_address,
          city,
          state,
          location,
          show_date,
          show_time,
          age_limit,
          ticket_price,
          ticket_url,
          genre,
          poster_id,
          approval_status,
          approval_count,
          required_approvals,
          is_featured,
          thank_you_count,
          report_count,
          is_hidden,
          created_at,
          updated_at,
          poster_type,
          poster:users!shows_poster_id_fkey (
            username,
            user_level,
            user_type,
            scene_points
          )
        `)
        .eq('approval_status', 'approved')
        .eq('is_hidden', false)
        .gte('show_date', new Date().toISOString().split('T')[0]) // Only future shows
        .order('show_date', { ascending: true });

      // Filter by cities within radius
      if (nearbyCities.length === 1) {
        // Single city query
        query = query
          .eq('city', location.city)
          .eq('state', location.state);
        console.log(`🔍 Single city query: city=${location.city}, state=${location.state}`);
      } else {
        // Multiple cities - use OR conditions with correct Supabase syntax
        const cityFilters = nearbyCities.map(c => 
          `and(city.eq.${c.city},state.eq.${c.state})`
        );
        const filterString = cityFilters.join(',');
        console.log(`🔍 Multi-city OR query: ${filterString}`);
        query = query.or(filterString);
      }

      // Execute query with retry logic
      const { data: showsData, error } = await executeWithRetry(async () => {
        return await query;
      }, 3, 2000);

      if (error) {
        console.error('Error loading shows:', error);
        this.isLoading = false;
        this.notifyListeners();
        return;
      }

      // Transform the data to match expected format
      this.shows = showsData.map(show => ({
        id: show.id,
        title: show.title,
        description: show.description,
        bandName: show.band_name,
        venueName: show.venue_name,
        venueAddress: show.venue_address,
        city: show.city,
        state: show.state,
        location: show.location,
        date: show.show_date,
        time: show.show_time,
        ageLimit: show.age_limit,
        ticketPrice: show.ticket_price,
        ticketUrl: show.ticket_url,
        genre: show.genre,
        posterId: show.poster_id,
        approvalStatus: show.approval_status,
        approvalCount: show.approval_count,
        requiredApprovals: show.required_approvals,
        isFeatured: show.is_featured,
        thankYouCount: show.thank_you_count,
        reportCount: show.report_count,
        isHidden: show.is_hidden,
        createdAt: show.created_at,
        updatedAt: show.updated_at,
        posterType: show.poster_type,
        poster: show.users ? {
          id: show.users.id,
          username: show.users.username,
          userLevel: show.users.user_level,
          userType: show.users.user_type,
          scenePoints: show.users.scene_points
        } : null
      }));

      console.log(`✅ Loaded ${this.shows.length} shows from ${nearbyCities.length} cities within ${radius} miles`);

      // Set up real-time subscription for future updates
      this.setupRealtimeSubscription(nearbyCities);

      this.currentLocation = location;
      this.currentRadius = radius;
      this.isLoading = false;
      this.notifyListeners();

    } catch (error) {
      console.error('Error setting up shows listener:', error);
      
      // Queue for retry if it's a network error
      if (NetworkService.isNetworkError(error)) {
        NetworkService.addToRetryQueue(() => this.loadShows(location, radius));
      }
      
      this.isLoading = false;
      this.notifyListeners();
    }
  }
  
  setupRealtimeSubscription(nearbyCities) {
    // Clean up existing subscription first
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  
    // Set up real-time subscription for show updates
    this.subscription = supabase
      .channel(`shows_changes_${Date.now()}`) // Unique channel name
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'shows',
          filter: 'approval_status=eq.approved'
        }, 
        (payload) => {
          console.log('🔄 Show updated:', payload);
          // Reload shows when there are changes
          this.loadShows(this.currentLocation, this.currentRadius);
        }
      )
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'show_reports'
        },
        (payload) => {
          console.log('🚩 Show report updated:', payload);
          // Reload shows when reports are added/updated
          this.loadShows(this.currentLocation, this.currentRadius);
        }
      )
      .subscribe();
  }

  async addShow(showData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { success: false, error: 'User must be logged in to add shows' };
      }

      // Validate required fields
      if (!showData.band_name || showData.band_name.trim().length < 1) {
        return { success: false, error: 'Band name must be at least 1 character.' };
      }

      if (!showData.venue_name || showData.venue_name.trim().length < 2) {
        return { success: false, error: 'Venue name must be at least 2 characters' };
      }

      // Validate time format
      if (showData.show_time && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(showData.show_time)) {
        return { success: false, error: 'Invalid time format. Use HH:MM 24-hour format' };
      }

      // Validate ticket price format
      if (showData.ticket_price && !/^[0-9]+(\.[0-9]{2})?$/.test(showData.ticket_price)) {
        return { success: false, error: 'Invalid ticket price format. Use numbers only (e.g., 15.00)' };
      }

      // Updated age restriction validation to only allow 'all-ages', '18+', or '21+'
      if (showData.age_limit && !['all-ages', '18+', '21+'].includes(showData.age_limit)) {
        return { success: false, error: 'Invalid age restriction. Must be one of: All Ages, 18+, 21+' };
      }

      const showRecord = {
        title: showData.band_name, // Use band_name as the title
        description: showData.description,
        band_name: showData.band_name,
        venue_name: showData.venue_name,
        venue_address: showData.venue_address || null, // Make address optional
        city: showData.city,
        state: showData.state,
        location: showData.location || null,
        show_date: showData.show_date,
        show_time: showData.show_time,
        age_limit: showData.age_limit,
        ticket_price: showData.ticket_price ? String(showData.ticket_price) : null,
        ticket_url: showData.ticket_url || null,
        genre: showData.genre ? this.mapGenreToEnum(showData.genre) : 'other', // Map genre to enum value
        poster_id: user.id,
        approval_status: 'pending',
        approval_count: 0,
        required_approvals: 3,
        is_featured: false,
        thank_you_count: 0,
        report_count: 0,
        is_hidden: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        poster_type: showData.poster_type || 'fan'
      };

      const { data, error } = await supabase
        .from('shows')
        .insert([showRecord])
        .select()
        .single();

      if (error) {
        console.error('Error adding show:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Show added with ID:', data.id);
      return { success: true, id: data.id };
    } catch (error) {
      console.error('Error adding show:', error);
      return { success: false, error: error.message };
    }
  }

  async getShow(showId) {
    try {
      const { data, error } = await supabase
        .from('shows')
        .select(`
          id,
          title,
          band_name,
          venue_name,
          show_date,
          show_time,
          description,
          genre,
          ticket_price,
          ticket_url,
          venue_address,
          city,
          state,
          location,
          poster_id,
          poster_type,
          approval_status,
          approval_count,
          required_approvals,
          is_featured,
          thank_you_count,
          report_count,
          is_hidden,
          created_at,
          updated_at,
          users:poster_id (
            username,
            user_level,
            user_type,
            scene_points
          )
        `)
        .eq('id', showId)
        .single();

      if (error) {
        console.error('Error getting show:', error);
        return null;
      }

      return Show.fromJson({
        id: data.id,
        band_name: data.band_name,
        venue_name: data.venue_name,
        show_date: data.show_date,
        show_time: data.show_time,
        description: data.description,
        genre: data.genre,
        ticket_price: data.ticket_price,
        ticket_url: data.ticket_url,
        venue_address: data.venue_address,
        city: data.city,
        state: data.state,
        location: data.location,
        poster: new ShowPoster({
          uid: data.poster_id,
          username: data.users?.username || 'User',
          type: data.poster_type || data.users?.user_type || 'fan',
          user_level: data.users?.user_level || null,
          scene_points: data.users?.scene_points || 0
        }),
        approval_status: data.approval_status,
        approval_count: data.approval_count,
        required_approvals: data.required_approvals,
        is_featured: data.is_featured,
        thank_you_count: data.thank_you_count,
        report_count: data.report_count,
        is_hidden: data.is_hidden,
        created_at: data.created_at,
        updated_at: data.updated_at
      });
    } catch (error) {
      console.error('Error getting show:', error);
      return null;
    }
  }

  // Filter shows based on criteria
  getFilteredShows({
    searchQuery = '',
    venue = '',
    selectedDate = null,
    radius = this.currentRadius,
    showUpcomingOnly = true
  } = {}) {
    let filteredShows = [...this.shows];

    // Filter by search query (bands, venue)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredShows = filteredShows.filter(show => 
        (show.bandName && show.bandName.toLowerCase().includes(query)) ||
        show.venueName.toLowerCase().includes(query)
      );
    }

    // Filter by venue
    if (venue) {
      filteredShows = filteredShows.filter(show => 
        show.venueName.toLowerCase().includes(venue.toLowerCase())
      );
    }

    // Filter by date
    if (selectedDate) {
      filteredShows = filteredShows.filter(show => 
        show.date === selectedDate
      );
    }

    // Filter by radius
    if (radius && radius !== 25) {
      filteredShows = filteredShows.filter(show => 
        !show.distanceFromUser || show.distanceFromUser <= radius
      );
    }

    // Filter upcoming shows only
    if (showUpcomingOnly) {
      const today = new Date().toISOString().split('T')[0];
      filteredShows = filteredShows.filter(show => show.date >= today);
    }

    return filteredShows;
  }

  getShowsByGenre(genre) {
    return this.shows.filter(show => show.genre === genre);
  }

  getShowsByVenue(venue) {
    return this.shows.filter(show => 
      show.venueName.toLowerCase().includes(venue.toLowerCase())
    );
  }

  getUpcomingShows(days = 7) {
    const today = new Date();
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + days);

    const todayStr = today.toISOString().split('T')[0];
    const futureDateStr = futureDate.toISOString().split('T')[0];

    return this.shows.filter(show => 
      show.date >= todayStr && show.date <= futureDateStr
    );
  }

  getUniqueVenues() {
    const venues = [...new Set(this.shows.map(show => show.venueName))];
    return venues.sort();
  }

  getUniqueGenres() {
    const allGenres = this.shows.reduce((genres, show) => {
      if (show.genre) {
        genres.push(...show.genre);
      }
      return genres;
    }, []);
    return [...new Set(allGenres)].sort();
  }

  async changeLocation(city, state) {
    this.currentLocation = { city, state };
    // The Home component will now trigger the reload.
  }

  async changeRadius(newRadius) {
    this.currentRadius = newRadius;
    // The Home component will now trigger the reload.
  }

  refreshShows() {
    this.loadShows(this.currentLocation, this.currentRadius);
  }

  async clearAllShows() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { success: false, error: 'User must be logged in' };
      }

      // Check if user is admin
      const { data: userData } = await supabase
        .from('users')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (!userData?.is_admin) {
        return { success: false, error: 'Only admins can clear all shows' };
      }

      const { error } = await supabase
        .from('shows')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all shows

      if (error) {
        console.error('Error clearing shows:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ All shows cleared');
      this.shows = [];
      this.notifyListeners();
      return { success: true };
    } catch (error) {
      console.error('Error clearing shows:', error);
      return { success: false, error: error.message };
    }
  }

  async removeDuplicateShows() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { success: false, error: 'User must be logged in' };
      }

      // Check if user is admin
      const { data: userData } = await supabase
        .from('users')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (!userData?.is_admin) {
        return { success: false, error: 'Only admins can remove duplicates' };
      }

      // Get all shows
      const { data: allShows, error: fetchError } = await supabase
        .from('shows')
        .select('id, title, band_name, venue_name, show_date')
        .order('created_at', { ascending: true });

      if (fetchError) {
        console.error('Error fetching shows for deduplication:', fetchError);
        return { success: false, error: fetchError.message };
      }

      // Find duplicates (same title, band_name, venue_name, show_date)
      const seen = new Set();
      const duplicates = [];

      allShows.forEach(show => {
        const key = `${show.title}-${show.band_name}-${show.venue_name}-${show.show_date}`;
        if (seen.has(key)) {
          duplicates.push(show.id);
        } else {
          seen.add(key);
        }
      });

      if (duplicates.length === 0) {
        console.log('No duplicate shows found');
        return { success: true, removed: 0 };
      }

      // Delete duplicates
      const { error: deleteError } = await supabase
        .from('shows')
        .delete()
        .in('id', duplicates);

      if (deleteError) {
        console.error('Error removing duplicates:', deleteError);
        return { success: false, error: deleteError.message };
      }

      console.log(`✅ Removed ${duplicates.length} duplicate shows`);
      this.refreshShows();
      return { success: true, removed: duplicates.length };
    } catch (error) {
      console.error('Error removing duplicates:', error);
      return { success: false, error: error.message };
    }
  }

  async deleteOldShows() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { success: false, error: 'User must be logged in' };
      }

      // Check if user is admin
      const { data: userData } = await supabase
        .from('users')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (!userData?.is_admin) {
        return { success: false, error: 'Only admins can delete old shows' };
      }

      // Delete shows older than 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const cutoffDate = thirtyDaysAgo.toISOString().split('T')[0];

      const { data: deletedShows, error } = await supabase
        .from('shows')
        .delete()
        .lt('show_date', cutoffDate)
        .select('id');

      if (error) {
        console.error('Error deleting old shows:', error);
        return { success: false, error: error.message };
      }

      const deletedCount = deletedShows?.length || 0;
      console.log(`✅ Deleted ${deletedCount} old shows`);
      this.refreshShows();
      return { success: true, deleted: deletedCount };
    } catch (error) {
      console.error('Error deleting old shows:', error);
      return { success: false, error: error.message };
    }
  }

  async createSampleShows() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { success: false, error: 'User must be logged in' };
      }

      const sampleShows = [
        {
          title: "Indie Rock Night",
          artist: "The Local Bands",
          venue: "The High Dive",
          date: "2024-12-15",
          time: "20:00",
          description: "A night of local indie rock bands",
          genre: ["indie", "rock"],
          ticket_price: 15.00,
          city: "Gainesville",
          state: "FL",
          poster_user_id: user.id,
          status: "approved"
        },
        {
          title: "Jazz & Blues Evening",
          artist: "Smooth Operators",
          venue: "The Top",
          date: "2024-12-20",
          time: "19:30",
          description: "Smooth jazz and blues performances",
          genre: ["jazz", "blues"],
          ticket_price: 20.00,
          city: "Gainesville",
          state: "FL",
          poster_user_id: user.id,
          status: "approved"
        },
        {
          title: "Electronic Dance Party",
          artist: "DJ Nightlife",
          venue: "Heartwood Soundstage",
          date: "2024-12-25",
          time: "22:00",
          description: "Electronic music and dancing all night",
          genre: ["electronic", "dance"],
          ticket_price: 25.00,
          city: "Gainesville",
          state: "FL",
          poster_user_id: user.id,
          status: "approved"
        }
      ];

      const { data, error } = await supabase
        .from('shows')
        .insert(sampleShows)
        .select();

      if (error) {
        console.error('Error creating sample shows:', error);
        return { success: false, error: error.message };
      }

      console.log(`✅ Created ${data.length} sample shows`);
      this.refreshShows();
      return { success: true, created: data.length };
    } catch (error) {
      console.error('Error creating sample shows:', error);
      return { success: false, error: error.message };
    }
  }

  addListener(callback) {
    this.listeners.push(callback);
    // Immediately notify with current state
    callback(this.shows, this.isLoading, this.currentLocation);
  }

  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.shows, this.isLoading, this.currentLocation);
      } catch (error) {
        console.error('Error in show listener:', error);
      }
    });
  }

  destroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.listeners = [];
    this.shows = [];
  }

  // =============================================
  // SHOW CREATION & MANAGEMENT
  // =============================================

  /**
   * Create a new show with proper validation and security
   */
  async createShow(showData, userId) {
    try {
      // Validate required fields
      const requiredFields = ['title', 'band_name', 'venue_name', 'city', 'state', 'show_date'];
      for (const field of requiredFields) {
        if (!showData[field]) {
          throw new Error(`Missing required field: ${field}`);
        }
      }

      // Get user info to determine poster type
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('user_type, scene_points')
        .eq('id', userId)
        .single();

      if (userError) {
        throw new Error('User not found');
      }

      // Sanitize and validate input
      const sanitizedShow = {
        title: this.sanitizeText(showData.title, 200),
        description: showData.description ? this.sanitizeText(showData.description, 2000) : null,
        band_name: this.sanitizeText(showData.band_name, 200),
        venue_name: this.sanitizeText(showData.venue_name, 200),
        venue_address: this.sanitizeText(showData.venue_address, 500),
        city: this.sanitizeText(showData.city, 100),
        state: this.sanitizeText(showData.state, 50),
        show_date: this.validateDate(showData.show_date),
        show_time: showData.show_time || null,
        age_limit: showData.age_limit ? this.sanitizeText(showData.age_limit, 20) : null,
        ticket_price: showData.ticket_price ? this.sanitizeText(showData.ticket_price, 50) : null,
        ticket_url: showData.ticket_url ? this.validateUrl(showData.ticket_url) : null,
        genre: this.validateGenre(showData.genre || 'other'),
        poster_id: userId,
        poster_type: user.user_type
      };

      // Add location if coordinates provided
      if (showData.latitude && showData.longitude) {
        sanitizedShow.location = `POINT(${showData.longitude} ${showData.latitude})`;
      }

      // Set approval requirements based on poster type (from FAQ)
      if (user.user_type === 'band' || user.user_type === 'venue') {
        // Auto-approve band/venue posts
        sanitizedShow.approval_status = 'approved';
        sanitizedShow.required_approvals = 0;
      } else {
        // Fan posts need 3 approvals
        sanitizedShow.approval_status = 'pending';
        sanitizedShow.required_approvals = 3;
      }

      const { data, error } = await supabase
        .from('shows')
        .insert([sanitizedShow])
        .select()
        .single();

      if (error) {
        console.error('Error creating show:', error);
        throw new Error(`Failed to create show: ${error.message}`);
      }

      this.notifyListeners();
      return { success: true, show: data };

    } catch (error) {
      console.error('Error in createShow:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================
  // SHOW DISCOVERY & FILTERING (from FAQ)
  // =============================================

  /**
   * Get shows with location-based filtering and radius support
   */
  async getShows(filters = {}) {
    try {
      let query = supabase
        .from('shows_with_poster')
        .select('*')
        .eq('approval_status', 'approved')
        .eq('is_hidden', false)
        .gte('show_date', new Date().toISOString().split('T')[0]); // Future shows only

      // Apply filters
      if (filters.city && filters.state) {
        query = query.eq('city', filters.city).eq('state', filters.state);
      }

      if (filters.genre && filters.genre !== 'all') {
        query = query.eq('genre', filters.genre);
      }

      if (filters.poster_type && filters.poster_type !== 'all') {
        query = query.eq('poster_type', filters.poster_type);
      }

      if (filters.venue_name) {
        query = query.ilike('venue_name', `%${filters.venue_name}%`);
      }

      if (filters.search) {
        const searchTerm = `%${filters.search}%`;
        query = query.or(`title.ilike.${searchTerm},band_name.ilike.${searchTerm},venue_name.ilike.${searchTerm},description.ilike.${searchTerm}`);
      }

      if (filters.date) {
        query = query.eq('show_date', filters.date);
      }

      // Order by date
      query = query.order('show_date', { ascending: true });

      // Apply limit
      const limit = Math.min(filters.limit || 50, 100); // Max 100 shows
      query = query.limit(limit);

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Apply radius filtering if location provided
      let filteredShows = data;
      if (filters.userLatitude && filters.userLongitude && filters.radius) {
        filteredShows = this.filterByRadius(data, filters.userLatitude, filters.userLongitude, filters.radius);
      }

      return { success: true, shows: filteredShows };

    } catch (error) {
      console.error('Error getting shows:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get shows for moderation queue
   */
  async getModerationQueue(userId) {
    try {
      // Check if user can moderate
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('is_admin, scene_points')
        .eq('id', userId)
        .single();

      if (userError || (!user.is_admin && user.scene_points < 100)) {
        throw new Error('Insufficient permissions to access moderation queue');
      }

      const { data, error } = await supabase
        .from('moderation_queue')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        throw error;
      }

      return { success: true, shows: data };

    } catch (error) {
      console.error('Error getting moderation queue:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's own shows
   */
  async getUserShows(userId) {
    try {
      const { data, error } = await supabase
        .from('shows_with_poster')
        .select('*')
        .eq('poster_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return { success: true, shows: data };

    } catch (error) {
      console.error('Error getting user shows:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's liked shows
   */
  async getLikedShows(userId) {
    try {
      const { data, error } = await supabase
        .from('user_likes')
        .select(`
          *,
          shows:show_id (
            *,
            users:poster_id (username, user_level, user_type, scene_points)
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const shows = data.map(like => like.shows).filter(show => show);
      return { success: true, shows };

    } catch (error) {
      console.error('Error getting liked shows:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================
  // SHOW APPROVAL SYSTEM (from FAQ)
  // =============================================

  /**
   * Approve a show (community moderation)
   */
  async approveShow(showId, userId) {
    try {
      // Check if show exists and is pending
      const { data: show, error: showError } = await supabase
        .from('shows')
        .select('poster_id, approval_status')
        .eq('id', showId)
        .single();

      if (showError || !show) {
        throw new Error('Show not found');
      }

      if (show.approval_status !== 'pending') {
        throw new Error('Show is not pending approval');
      }

      // Can't approve own shows
      if (show.poster_id === userId) {
        throw new Error('Cannot approve your own shows');
      }

      // Check if user already approved this show
      const { data: existingApproval } = await supabase
        .from('show_approvals')
        .select('id')
        .eq('show_id', showId)
        .eq('approver_id', userId)
        .single();

      if (existingApproval) {
        throw new Error('You have already approved this show');
      }

      // Create approval record (trigger will handle the rest)
      const { error: approvalError } = await supabase
        .from('show_approvals')
        .insert([{
          show_id: showId,
          approver_id: userId
        }]);

      if (approvalError) {
        throw approvalError;
      }

      // After approval record is created, award points to the poster if they are a fan
      const { data: showDetails, error: showDetailsError } = await supabase
        .from('shows')
        .select('poster_id')
        .eq('id', showId)
        .single();
      if (!showDetailsError && showDetails?.poster_id) {
        // Get poster user type
        const { data: poster, error: posterError } = await supabase
          .from('users')
          .select('type')
          .eq('id', showDetails.poster_id)
          .single();
        if (!posterError && poster?.type === 'fan') {
          await awardScenePoints(showDetails.poster_id, 1, 'Show approved by moderation', showId);
        }
      }

      this.notifyListeners();
      return { success: true };

    } catch (error) {
      console.error('Error approving show:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Reject a show (moderators only)
   */
  async rejectShow(showId, userId, reason) {
    try {
      // Check if user can moderate
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('is_admin, scene_points')
        .eq('id', userId)
        .single();

      if (userError || (!user.is_admin && user.scene_points < 250)) {
        throw new Error('Insufficient permissions to reject shows');
      }

      const { error } = await supabase
        .from('shows')
        .update({ 
          approval_status: 'rejected',
          is_hidden: true 
        })
        .eq('id', showId);

      if (error) {
        throw error;
      }

      // After show is rejected, deduct points from the poster if they are a fan
      const { data: showDetails, error: showDetailsError } = await supabase
        .from('shows')
        .select('poster_id')
        .eq('id', showId)
        .single();
      if (!showDetailsError && showDetails?.poster_id) {
        // Get poster user type
        const { data: poster, error: posterError } = await supabase
          .from('users')
          .select('type')
          .eq('id', showDetails.poster_id)
          .single();
        if (!posterError && poster?.type === 'fan') {
          await awardScenePoints(showDetails.poster_id, -2, 'Show rejected by moderation', showId);
        }
      }

      this.notifyListeners();
      return { success: true };

    } catch (error) {
      console.error('Error rejecting show:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================
  // REPORTING SYSTEM (from FAQ)
  // =============================================

  /**
   * Report a show for inappropriate content
   */
  async reportShow(showId, userId, category, message) {
    try {
      // Validate report category
      const validCategories = ['spam', 'inappropriate_content', 'wrong_information', 'harassment', 'scam', 'other'];
      if (!validCategories.includes(category)) {
        throw new Error('Invalid report category');
      }

      // Check if user already reported this show
      const { data: existingReport } = await supabase
        .from('show_reports')
        .select('id')
        .eq('show_id', showId)
        .eq('reporter_id', userId)
        .single();

      if (existingReport) {
        throw new Error('You have already reported this show');
      }

      // Create report (trigger will handle the rest)
      const { error } = await supabase
        .from('show_reports')
        .insert([{
          show_id: showId,
          reporter_id: userId,
          category,
          message: this.sanitizeReportMessage(message)
        }]);

      if (error) {
        throw error;
      }

      this.notifyListeners();
      return { success: true };

    } catch (error) {
      console.error('Error reporting show:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================
  // LIKE/THANK SYSTEM (from FAQ)
  // =============================================

  /**
   * Like/save a show
   */
  async likeShow(showId, userId) {
    try {
      // Check if already liked - use maybeSingle() to avoid 406 error when no record exists
      const { data: existingLike, error: checkError } = await supabase
        .from('user_likes')
        .select('id')
        .eq('show_id', showId)
        .eq('user_id', userId)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking like status:', checkError);
        throw checkError;
      }

      if (existingLike) {
        // Unlike - delete the existing like
        const { error } = await supabase
          .from('user_likes')
          .delete()
          .eq('id', existingLike.id);

        if (error) throw error;
        return { success: true, liked: false };
      } else {
        // Like - create new like record
        const { error } = await supabase
          .from('user_likes')
          .insert([{ show_id: showId, user_id: userId }]);

        if (error) throw error;
        return { success: true, liked: true };
      }

    } catch (error) {
      console.error('Error liking show:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Thank a fan poster (awards 2 scene points)
   */
  async thankShow(showId, userId) {
    try {
      // Check if show is by a fan and not own post
      const { data: show, error: showError } = await supabase
        .from('shows')
        .select('poster_id, poster_type')
        .eq('id', showId)
        .single();

      if (showError || !show) {
        throw new Error('Show not found');
      }

      if (show.poster_type !== 'fan') {
        throw new Error('Can only thank fan posts');
      }

      if (show.poster_id === userId) {
        throw new Error('Cannot thank your own posts');
      }

      // Check if already thanked - use maybeSingle() to avoid 406 error when no record exists
      const { data: existingThanks, error: checkError } = await supabase
        .from('show_thanks')
        .select('id')
        .eq('show_id', showId)
        .eq('thanker_id', userId)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking thank status:', checkError);
        throw checkError;
      }

      if (existingThanks) {
        throw new Error('You have already thanked this show');
      }

      // Create thank you record (trigger will handle points)
      const { error } = await supabase
        .from('show_thanks')
        .insert([{
          show_id: showId,
          thanker_id: userId
        }]);

      if (error) {
        throw error;
      }

      return { success: true };

    } catch (error) {
      console.error('Error thanking show:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================
  // UTILITY FUNCTIONS
  // =============================================

  /**
   * Filter shows by radius (from FAQ)
   */
  filterByRadius(shows, userLat, userLng, radiusMiles) {
    return shows.filter(show => {
      if (!show.location) return true; // Include shows without location
      
      // Parse PostGIS POINT format
      const locationMatch = show.location.match(/POINT\(([^)]+)\)/);
      if (!locationMatch) return true;
      
      const [lng, lat] = locationMatch[1].split(' ').map(Number);
      const distance = this.calculateDistance(userLat, userLng, lat, lng);
      
      return distance <= radiusMiles;
    });
  }

  /**
   * Calculate distance between two points in miles
   */
  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  // =============================================
  // VALIDATION & SANITIZATION
  // =============================================

  /**
   * Sanitize text input with length limits
   */
  sanitizeText(text, maxLength = 500) {
    // Allow null or empty address
    if (text === null || text === undefined) return null;
    // Remove HTML tags and trim
    const sanitized = text.replace(/<[^>]*>/g, '').trim();
    if (sanitized.length === 0) return null;
    if (sanitized.length > maxLength) {
      throw new Error(`Text must be less than ${maxLength} characters`);
    }
    return sanitized;
  }

  /**
   * Sanitize report message with minimum length requirement
   */
  sanitizeReportMessage(text, maxLength = 1000) {
    if (text === null || text === undefined) {
      return 'Report submitted for review';
    }
    // Remove HTML tags and trim
    const sanitized = text.replace(/<[^>]*>/g, '').trim();
    if (sanitized.length === 0) {
      return 'Report submitted for review';
    }
    if (sanitized.length < 10) {
      return 'Report submitted for review';
    }
    if (sanitized.length > maxLength) {
      throw new Error(`Message must be less than ${maxLength} characters`);
    }
    return sanitized;
  }

  /**
   * Validate date (must be future)
   */
  validateDate(dateString) {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date format');
    }
    
    if (date < today) {
      throw new Error('Show date must be in the future');
    }
    
    return dateString;
  }

  /**
   * Validate URL
   */
  validateUrl(url) {
    try {
      const urlObj = new URL(url);
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        throw new Error('URL must use HTTP or HTTPS');
      }
      return url;
    } catch {
      throw new Error('Invalid URL format');
    }
  }

  /**
   * Validate music genre
   */
  validateGenre(genre) {
    if (!this.MUSIC_GENRES.includes(genre)) {
      throw new Error('Invalid music genre');
    }
    return genre;
  }

  /**
   * Map frontend genre to database enum value
   */
  mapGenreToEnum(genre) {
    return this.GENRE_MAPPING[genre] || 'other';
  }

  async loadShowsPaginated(location = this.currentLocation, radius = 25, page = 1, pageSize = 10, filters = {}) {
    // Prevent multiple rapid calls
    if (this.isLoading) {
      console.log('🔄 Already loading shows, skipping duplicate request');
      return;
    }

    // Debounce rapid calls
    const now = Date.now();
    if (this.lastLoadTime && (now - this.lastLoadTime) < 1000) {
      console.log('🔄 Debouncing rapid loadShows calls');
      return;
    }
    this.lastLoadTime = now;

    try {
      this.isLoading = true;
      this.notifyListeners();

      if (!location) {
        console.warn('No location provided for loadShowsPaginated');
        this.isLoading = false;
        this.notifyListeners();
        return;
      }

      const nearbyCities = CityDistanceService.getCitiesWithinRadius(
        location.city, 
        location.state, 
        radius
      );

      console.log(`🌍 Loading shows page ${page} (${pageSize} per page) within ${radius} miles of ${location.city}, ${location.state}`);
      console.log(`📍 Found ${nearbyCities.length} cities:`, nearbyCities.map(c => `${c.city}, ${c.state} (${c.distance}mi)`));

      // Calculate offset for pagination
      const offset = (page - 1) * pageSize;

      // Build base query with pagination
      let query = supabase
        .from('shows')
        .select(`
          id,
          title,
          description,
          band_name,
          venue_name,
          venue_address,
          city,
          state,
          location,
          show_date,
          show_time,
          age_limit,
          ticket_price,
          ticket_url,
          genre,
          poster_id,
          approval_status,
          approval_count,
          required_approvals,
          is_featured,
          thank_you_count,
          report_count,
          is_hidden,
          created_at,
          updated_at,
          poster_type,
          poster:users!shows_poster_id_fkey (
            id,
            username,
            user_level,
            user_type,
            scene_points
          )
        `, { count: 'exact' }) // Get total count for pagination
        .eq('approval_status', 'approved')
        .eq('is_hidden', false)
        .gte('show_date', new Date().toISOString().split('T')[0]) // Only future shows
        .order('show_date', { ascending: true })
        .order('show_time', { ascending: true, nullsLast: true })
        .range(offset, offset + pageSize - 1); // Database-level pagination

      // Apply filters at database level
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        query = query.or(`band_name.ilike.%${searchTerm}%,venue_name.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
      }

      if (filters.genre && filters.genre !== '') {
        query = query.eq('genre', filters.genre);
      }

      if (filters.dateFrom) {
        query = query.gte('show_date', filters.dateFrom);
      }

      if (filters.dateTo) {
        query = query.lte('show_date', filters.dateTo);
      }

      if (filters.posterType && filters.posterType !== '') {
        query = query.eq('poster_type', filters.posterType);
      }

      // Filter by cities within radius
      if (nearbyCities.length === 1) {
        // Single city query
        query = query
          .eq('city', location.city)
          .eq('state', location.state);
        console.log(`🔍 Single city query: city=${location.city}, state=${location.state}`);
      } else {
        // Multiple cities - use OR conditions with correct Supabase syntax
        const cityFilters = nearbyCities.map(c => 
          `and(city.eq.${c.city},state.eq.${c.state})`
        );
        const filterString = cityFilters.join(',');
        console.log(`🔍 Multi-city OR query: ${filterString}`);
        query = query.or(filterString);
      }

      // Execute query
      const { data: showsData, error, count } = await query;

      if (error) {
        console.error('Error loading paginated shows:', error);
        this.isLoading = false;
        this.notifyListeners();
        return;
      }

      // If shows are loaded, fetch their like/thank status for the current user
      let likedShowsSet = new Set();
      let thankedShowsSet = new Set();
      const user = authService.user;

      if (user && showsData && showsData.length > 0) {
        const showIds = showsData.map(s => s.id);

        // Fetch likes for these shows
        const { data: likesData } = await supabase
          .from('user_likes')
          .select('show_id')
          .eq('user_id', user.id)
          .in('show_id', showIds);
        
        if (likesData) {
          likedShowsSet = new Set(likesData.map(l => l.show_id));
        }

        // Fetch thanks for these shows
        const { data: thanksData } = await supabase
          .from('show_thanks')
          .select('show_id')
          .eq('thanker_id', user.id)
          .in('show_id', showIds);

        if (thanksData) {
          thankedShowsSet = new Set(thanksData.map(t => t.show_id));
        }
      }

      // Convert to Show objects and add distance info
      this.shows = showsData.map(showData => {
        const show = Show.fromJson({
          ...showData, // Pass all original data
          is_liked: likedShowsSet.has(showData.id),
          is_thanked: thankedShowsSet.has(showData.id),
          posterUsername: showData.poster?.username || 'Unknown User',
          poster: {
            uid: showData.poster?.id || showData.poster_id,
            username: showData.poster?.username || 'User',
            type: showData.poster_type || showData.poster?.user_type || 'fan',
            user_level: showData.poster?.user_level || null,
            scene_points: showData.poster?.scene_points || 0
          },
        });

        // Add distance info for shows from nearby cities
        const cityInfo = nearbyCities.find(c => c.city === showData.city && c.state === showData.state);
        if (cityInfo) {
          show.distanceFromUser = cityInfo.distance;
        }

        return show;
      });

      // Store pagination metadata
      this.paginationInfo = {
        currentPage: page,
        pageSize: pageSize,
        totalShows: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        hasNextPage: page * pageSize < (count || 0),
        hasPreviousPage: page > 1
      };

      console.log(`✅ Loaded ${this.shows.length} shows (page ${page}/${this.paginationInfo.totalPages}) from ${nearbyCities.length} cities within ${radius} miles`);

      this.currentLocation = location;
      this.currentRadius = radius;
      this.isLoading = false;
      this.notifyListeners();

    } catch (error) {
      console.error('Error setting up paginated shows listener:', error);
      this.isLoading = false;
      this.notifyListeners();
    }
  }

  getPaginationInfo() {
    return this.paginationInfo;
  }
}

// Create singleton instance
const showService = new ShowService();
export default showService; 